import { defineStore } from 'pinia';
import _ from 'lodash';
import Vue from 'vue';

import BillingApi from '@/store/api/billing';

export const useBillingStore = defineStore('billing', {
    state: () => ({
        loading: false,
        dashboard: {
            scope: 'system',
            type: 'billing',
            variables: {
                paginationSizes: [25, 50, 100],
                order: 'name',
                direction: 'asc',
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 1,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: [],
        },
        detail: {
            cms: null,
            summary: null,
            currentPeriod: null,
            pricing: [],
            prorationOptions: null,
            unbilledTickets: [],
            // what an invoice prints when the CMS has no billing address of its own
            billingPartyRecipient: null,
        },
        history: {
            items: [],
            pageInfo: {
                total: 0,
                perPage: 10,
                currentPage: 1,
                lastPage: 1,
            },
        },
        features: [],
        providerCosts: {
            loading: false,
            entries: [],
            tokenPrices: [],
        },
    }),

    getters: {
        isLoading: (state) => state.loading,
        dashboardItems: (state) => state.dashboard.items,
        dashboardPageInfo: (state) => state.dashboard.pageInfo,
        dashboardConfig: (state) => ({
            allColumns: state.dashboard.allColumns,
            activeColumns: state.dashboard.activeColumns,
            showFilters: state.dashboard.showFilters,
            link: state.dashboard.link,
        }),
        detailCms: (state) => state.detail.cms,
        detailSummary: (state) => state.detail.summary,
        currentPeriod: (state) => state.detail.currentPeriod,
        pricing: (state) => state.detail.pricing,
        historyItems: (state) => state.history.items,
        historyPageInfo: (state) => state.history.pageInfo,
        allFeatures: (state) => state.features,
        prorationOptions: (state) => state.detail.prorationOptions,
        unbilledTickets: (state) => state.detail.unbilledTickets,
        billingPartyRecipient: (state) => state.detail.billingPartyRecipient,
        providerCostEntries: (state) => state.providerCosts.entries,
        providerCostsLoading: (state) => state.providerCosts.loading,
        providerTokenPrices: (state) => state.providerCosts.tokenPrices,
    },

    actions: {
        setLoading(loading) {
            this.loading = loading;
        },

        setDashboardData(data) {
            if (data.pageInfo) {
                this.dashboard.pageInfo = data.pageInfo;
            }
            if (data.config) {
                this.dashboard.allColumns = _.get(data, 'config.allColumns', []);
                this.dashboard.showFilters = _.get(data, 'config.showFilters', false);
                this.dashboard.defaultSort = _.get(data, 'config.defaultSort') || null;
                this.dashboard.link = _.get(data, 'config.link', {});

                // Filter allColumns to get full column objects for activeColumns
                const activeColumnKeys = _.get(data, 'config.activeColumnKeys', []);
                let activeColumns = [];
                _.each(this.dashboard.allColumns, (column) => {
                    const allowed = _.find(activeColumnKeys, (keyObj) => column.key === _.get(keyObj, 'key'));
                    if (allowed) activeColumns.push(column);
                });
                if (activeColumns.length) {
                    this.dashboard.activeColumns = activeColumns;
                }
            }
            if (data.items) {
                this.dashboard.items = data.items;
            }
        },

        defineItemsPerPage(perPage) {
            this.dashboard.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.dashboard.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (
                value.match === '' &&
                value.like === '' &&
                value.from === '' &&
                value.to === '' &&
                (!value.in || value.in.length === 0)
            ) {
                Vue.delete(this.dashboard.filters, key);
            } else {
                Vue.set(this.dashboard.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            this.dashboard.variables.order = key;
            this.dashboard.variables.direction = value;
        },

        setDetail(detail) {
            this.detail.cms = _.get(detail, 'cms', null);
            this.detail.summary = _.get(detail, 'summary', null);
            this.detail.currentPeriod = _.get(detail, 'currentPeriod', null);
            this.detail.pricing = _.get(detail, 'pricing', []);
            this.detail.prorationOptions = _.get(detail, 'prorationOptions', null);
            this.detail.unbilledTickets = _.get(detail, 'unbilledTickets', []) || [];
            this.detail.billingPartyRecipient = _.get(detail, 'billingPartyRecipient', null);
        },

        setCurrentPeriod(period) {
            this.detail.currentPeriod = period;

            // Update summary's currentPeriodTotal to match the new line items
            if (this.detail.summary && period && period.lineItems) {
                const total = period.lineItems.reduce((sum, item) => {
                    return sum + (parseFloat(_.get(item, 'total_price', 0)) || 0);
                }, 0);
                this.detail.summary.currentPeriodTotal = total;
            }
        },

        updatePricingItem(updatedPricing) {
            const featureId = _.get(updatedPricing, 'feature.id');
            const index = this.detail.pricing.findIndex((p) => _.get(p, 'feature.id') === featureId);
            if (index !== -1) {
                Vue.set(this.detail.pricing, index, {
                    ...this.detail.pricing[index],
                    customPrice: _.get(updatedPricing, 'customPrice', _.get(updatedPricing, 'price')),
                    effectivePrice: _.get(updatedPricing, 'effectivePrice', this.detail.pricing[index].effectivePrice),
                    enabled: _.get(updatedPricing, 'enabled', this.detail.pricing[index].enabled),
                    hasCustomPrice: _.get(updatedPricing, 'hasCustomPrice', this.detail.pricing[index].hasCustomPrice),
                    extensionEnabled: _.get(
                        updatedPricing,
                        'extensionEnabled',
                        this.detail.pricing[index].extensionEnabled
                    ),
                    extensionSupported: _.get(
                        updatedPricing,
                        'extensionSupported',
                        this.detail.pricing[index].extensionSupported
                    ),
                });
            }
        },

        setHistory(data) {
            this.history.items = _.get(data, 'items', []);
            this.history.pageInfo = _.get(data, 'pageInfo', this.history.pageInfo);
        },

        // The whole period from the response replaces the row, not just its
        // status: generating an invoice also gives the row its invoice, and
        // that is what decides whether the PDF button shows.
        replacePeriod(updatedPeriod) {
            const index = this.history.items.findIndex((item) => item.id === updatedPeriod.id);
            if (index !== -1) {
                Vue.set(this.history.items, index, {
                    ...this.history.items[index],
                    ...updatedPeriod,
                });
            }
        },

        setFeatures(features) {
            this.features = features;
        },

        addLineItem(lineItem) {
            if (this.detail.currentPeriod && this.detail.currentPeriod.lineItems) {
                this.detail.currentPeriod.lineItems.push(lineItem);
            }
        },

        updateLineItemInState(updatedLineItem) {
            if (this.detail.currentPeriod && this.detail.currentPeriod.lineItems) {
                const index = this.detail.currentPeriod.lineItems.findIndex((item) => item.id === updatedLineItem.id);
                if (index !== -1) {
                    Vue.set(this.detail.currentPeriod.lineItems, index, updatedLineItem);
                }
            }
        },

        removeLineItem(lineItemId) {
            if (this.detail.currentPeriod && this.detail.currentPeriod.lineItems) {
                const index = this.detail.currentPeriod.lineItems.findIndex((item) => item.id === lineItemId);
                if (index !== -1) {
                    this.detail.currentPeriod.lineItems.splice(index, 1);
                }
            }
        },

        updateCmsInterval(cms) {
            if (this.detail.cms && this.detail.cms.id === cms.id) {
                this.detail.cms.billing_period = cms.billing_period;
            }
            if (this.detail.summary) {
                this.detail.summary.interval = cms.billing_period;
            }
        },

        updateCmsBillingSettings(cms) {
            if (this.detail.cms && this.detail.cms.id === cms.id) {
                this.detail.cms = cms;
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.dashboard, 'showFilters', value);
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.dashboard.allColumns, (column) => {
                const allowed = _.find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.dashboard.activeColumns = activeColumns;
            }
        },

        // Async actions
        async fetchDashboard() {
            this.setLoading(true);

            try {
                const data = await BillingApi.fetchDashboard(this.dashboard);
                this.setDashboardData(data);
                return data;
            } catch (error) {
                console.error('Error fetching billing dashboard:', error);
                throw error;
            } finally {
                this.setLoading(false);
            }
        },

        async fetchDetail(cmsId) {
            this.setLoading(true);

            try {
                const data = await BillingApi.fetchDetail(cmsId);
                this.setDetail(data);
                return data;
            } catch (error) {
                console.error('Error fetching billing detail:', error);
                throw error;
            } finally {
                this.setLoading(false);
            }
        },

        async fetchMyDetail(cmsId) {
            this.setLoading(true);

            try {
                const data = await BillingApi.fetchMyDetail(cmsId);
                this.setDetail(data);
                return data;
            } catch (error) {
                console.error('Error fetching billing detail:', error);
                throw error;
            } finally {
                this.setLoading(false);
            }
        },

        async fetchHistory(variables = {}) {
            this.setLoading(true);

            const queryVariables = {
                cmsId: variables.cmsId,
                currentPage: _.get(variables, 'currentPage', this.history.pageInfo.currentPage),
                perPage: _.get(variables, 'perPage', this.history.pageInfo.perPage),
            };

            try {
                const data = await BillingApi.fetchHistory(queryVariables);
                this.setHistory(data);
                return data;
            } catch (error) {
                console.error('Error fetching billing history:', error);
                throw error;
            } finally {
                this.setLoading(false);
            }
        },

        async fetchFeatures(type = null) {
            try {
                const data = await BillingApi.fetchFeatures(type);
                this.setFeatures(data);
                return data;
            } catch (error) {
                console.error('Error fetching billing features:', error);
                throw error;
            }
        },

        async fetchFeatureMargins(type = null) {
            try {
                const data = await BillingApi.fetchFeatureMargins(type);
                this.setFeatures(data);
                return data;
            } catch (error) {
                console.error('Error fetching billing feature margins:', error);
                throw error;
            }
        },

        async updatePricing({ cmsId, pricing }) {
            const data = await BillingApi.updatePricing({ cmsId, pricing });
            this.setDetail(data);
            return data;
        },

        async fetchProviderCostSummary({ from, to } = {}) {
            this.providerCosts.loading = true;
            try {
                const data = await BillingApi.fetchProviderCostSummary({ from, to });
                this.providerCosts.entries = data || [];
                return data;
            } catch (error) {
                console.error('Error fetching provider cost summary:', error);
                throw error;
            } finally {
                this.providerCosts.loading = false;
            }
        },

        async fetchProviderTokenPrices() {
            try {
                const data = await BillingApi.fetchProviderTokenPrices();
                this.providerCosts.tokenPrices = data || [];
                return data;
            } catch (error) {
                console.error('Error fetching provider token prices:', error);
                throw error;
            }
        },

        async updateFeatureCostMargin({ billingFeatureCostId, marginMultiplier }) {
            const data = await BillingApi.updateFeatureCostMargin({ billingFeatureCostId, marginMultiplier });
            _.each(this.features, (feature) => {
                const existing = _.find(feature.costs, { id: data.id });
                if (existing) Vue.set(existing, 'margin_multiplier', data.margin_multiplier);
            });
            return data;
        },

        async updateInterval({ cmsId, interval }) {
            try {
                const data = await BillingApi.updateInterval({ cmsId, interval });
                this.updateCmsInterval(data);
                return data;
            } catch (error) {
                console.error('Error updating interval:', error);
                throw error;
            }
        },

        async updateBillingSettings(variables) {
            try {
                const data = await BillingApi.updateBillingSettings(variables);
                this.updateCmsBillingSettings(data);
                return data;
            } catch (error) {
                console.error('Error updating billing settings:', error);
                throw error;
            }
        },

        async closePeriod({ billingPeriodId, createNew = true, prorateMonths = null }) {
            try {
                const data = await BillingApi.closePeriod({ billingPeriodId, createNew, prorateMonths });
                // Always refresh detail to show updated state (closed period or new period)
                if (this.detail.cms) {
                    await this.fetchDetail(this.detail.cms.id);
                }
                return data;
            } catch (error) {
                console.error('Error closing period:', error);
                throw error;
            }
        },

        async createLineItem(variables) {
            try {
                const data = await BillingApi.createLineItem(variables);
                this.addLineItem(data);
                return data;
            } catch (error) {
                console.error('Error creating line item:', error);
                throw error;
            }
        },

        async reinitializeRecurringCharges({ billingPeriodId }) {
            try {
                const data = await BillingApi.reinitializeRecurringCharges({ billingPeriodId });
                this.setCurrentPeriod(data);
                return data;
            } catch (error) {
                console.error('Error reinitializing recurring charges:', error);
                throw error;
            }
        },

        async createPeriod({ cmsId }) {
            try {
                const data = await BillingApi.createPeriod({ cmsId });
                this.setCurrentPeriod(data);
                // Refresh the detail to get updated summary, etc.
                if (this.detail.cms) {
                    await this.fetchDetail(this.detail.cms.id);
                }
                return data;
            } catch (error) {
                console.error('Error creating billing period:', error);
                throw error;
            }
        },

        async markPeriodInvoiced({ billingPeriodId }) {
            try {
                const data = await BillingApi.markPeriodInvoiced({ billingPeriodId });
                this.replacePeriod(data);
                return data;
            } catch (error) {
                console.error('Error marking period as invoiced:', error);
                throw error;
            }
        },

        async retryInvoiceCharge({ invoiceId }) {
            try {
                const invoice = await BillingApi.retryInvoiceCharge({ invoiceId });
                const period = this.history.items.find((item) => _.get(item, 'invoice.id') === invoice.id);
                if (period) {
                    this.replacePeriod({ ...period, invoice });
                }
                return invoice;
            } catch (error) {
                console.error('Error charging the invoice again:', error);
                throw error;
            }
        },

        async markInvoicePaid({ invoiceId }) {
            try {
                const invoice = await BillingApi.markInvoicePaid({ invoiceId });
                const period = this.history.items.find((item) => _.get(item, 'invoice.id') === invoice.id);
                if (period) {
                    this.replacePeriod({ ...period, invoice });
                }
                return invoice;
            } catch (error) {
                console.error('Error marking the invoice paid:', error);
                throw error;
            }
        },

        // Both write manual positions (tickets, whole projects, free positions);
        // the detail is refetched because the unbilled list and the period change together.
        async billManualPositions({ billingPeriodId, positions }) {
            try {
                const data = await BillingApi.billManualPositions({ billingPeriodId, positions });
                if (this.detail.cms) {
                    await this.fetchDetail(this.detail.cms.id);
                }
                return data;
            } catch (error) {
                console.error('Error writing the positions:', error);
                throw error;
            }
        },

        async createOneOffInvoice({ cmsId, positions, sendEmail }) {
            try {
                const data = await BillingApi.createOneOffInvoice({ cmsId, positions, sendEmail });
                await this.fetchDetail(cmsId);
                return data;
            } catch (error) {
                console.error('Error creating the one-off invoice:', error);
                throw error;
            }
        },

        async generatePeriodInvoice({ billingPeriodId }) {
            try {
                const data = await BillingApi.generatePeriodInvoice({ billingPeriodId });
                this.replacePeriod(data);
                return data;
            } catch (error) {
                console.error('Error generating the invoice:', error);
                throw error;
            }
        },

        async updateLineItem(variables) {
            try {
                const data = await BillingApi.updateLineItem(variables);
                this.updateLineItemInState(data);
                return data;
            } catch (error) {
                console.error('Error updating line item:', error);
                throw error;
            }
        },

        async deleteLineItem({ id }) {
            try {
                const data = await BillingApi.deleteLineItem({ id });
                this.removeLineItem(id);
                return data;
            } catch (error) {
                console.error('Error deleting line item:', error);
                throw error;
            }
        },
    },
});
